import { getExtensionValue, MedplumClient } from "@medplum/core";
import {
  Consent,
  Patient,
  Practitioner,
  Questionnaire,
  QuestionnaireResponse,
  Reference,
  UsageContext,
} from "@medplum/fhirtypes";

import * as Sentry from "@sentry/react";
import { ZodObject, ZodRawShape } from "zod";

import {
  areAllRequiredQuestionsAnswered,
  flattenValueChangesIntakeObject,
  markModifiedQuestionnaireResponseItemsAsUnaccepted,
  questionnaireResponseIsAccepted,
} from "@/bots/intake-forms/intake-forms";
import { fetchPreviousConsentsByQuestionnaireId } from "@/consents/services/consents";
import { Extensions } from "@/fhir/enums/extensions";
import {
  FormsLibraryContext,
  FormsLibrarySection,
  QuiltedHealthCodeSystems,
} from "@/fhir/enums/quilted-health";
import { setAccounts } from "@/utils/accounts";
import { getResourcesFromBundle, joinReferenceString, setExtensionValue } from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";
import {
  getQuestionnaireResponseFromFormValues,
  QuestionnaireFormValues,
} from "@/utils/questionnaires";

export const getQuestionnaire = async (
  questionnaireId: string,
  medplum: MedplumClient,
): Promise<Questionnaire | undefined> => {
  try {
    const { bundle: questionnaireBundle } = await medplum.searchResources("Questionnaire", {
      _id: questionnaireId,
      status: "active",
    });

    return getResourcesFromBundle("Questionnaire", questionnaireBundle)[0];
  } catch (err) {
    Sentry.captureException(err);
    showSystemErrorNotification();
    throw err;
  }
};

export const getQuestionnaireResponse = async (
  questionnaireId: string,
  clientId: string,
  medplum: MedplumClient,
  taskId?: string,
): Promise<QuestionnaireResponse | undefined> => {
  const taskReference = taskId && joinReferenceString("Task", taskId);
  try {
    const { bundle: questionnaireResponseBundle } = await medplum.searchResources(
      "QuestionnaireResponse",
      {
        patient: joinReferenceString("Patient", clientId),
        questionnaire: joinReferenceString("Questionnaire", questionnaireId),
        _sort: "-_lastUpdated",
        _count: 1,
      },
    );
    const response = getResourcesFromBundle(
      "QuestionnaireResponse",
      questionnaireResponseBundle,
    )[0];

    if (
      response?.extension?.find(
        (extension) => extension.valueReference?.reference !== taskReference,
      )
    ) {
      return undefined;
    }

    return response;
  } catch (err) {
    Sentry.captureException(err);
    showSystemErrorNotification();
    throw err;
  }
};

export async function upsertQuestionnaireResponse(
  medplum: MedplumClient,
  patientReference: { reference: string },
  profileReference: Reference<Patient | Practitioner>,
  formSchema: ZodObject<ZodRawShape>,
  questionnaireValues: QuestionnaireFormValues,
  questionnaireResponse: QuestionnaireResponse | undefined,
  questionnaire: Questionnaire,
  encounterId?: string,
  taskId?: string | null,
) {
  const parsedValues = formSchema.parse(questionnaireValues);

  if (!questionnaireResponse) {
    questionnaireResponse = {
      resourceType: "QuestionnaireResponse",
      status:
        getQuestionnaireContext(questionnaire.useContext) === FormsLibrarySection.ConsentForms
          ? "completed"
          : "in-progress",
      extension: [],
    };
  }

  markModifiedQuestionnaireResponseItemsAsUnaccepted(
    flattenValueChangesIntakeObject(parsedValues),
    questionnaireResponse.item,
  );

  const allRequiredAnswered = areAllRequiredQuestionsAnswered(
    questionnaire?.item,
    questionnaireResponse.item,
  );

  const isAccepted = questionnaireResponseIsAccepted(questionnaireResponse.item);

  questionnaireResponse.extension = questionnaireResponse.extension ?? [];

  questionnaireResponse.extension = setExtensionValue(
    questionnaireResponse.extension,
    Extensions.QuestionnaireResponseIsCompleted,
    { valueBoolean: allRequiredAnswered && isAccepted },
  );

  const version = getExtensionValue(
    questionnaireResponse,
    Extensions.QuestionnaireResponseNamedVersion,
  )?.toString();

  const nextVersion = version ? `v${parseInt(version.slice(1), 10) + 1 || 1}` : "v1";

  questionnaireResponse.extension = setExtensionValue(
    questionnaireResponse.extension,
    Extensions.QuestionnaireResponseNamedVersion,
    { valueString: nextVersion },
  );

  const questionnaireResponseData = getQuestionnaireResponseFromFormValues(
    questionnaire,
    questionnaireValues,
    patientReference,
    profileReference,
    questionnaireResponse,
  );

  // Add encounter reference if provided
  if (encounterId) {
    questionnaireResponseData.encounter = {
      reference: joinReferenceString("Encounter", encounterId),
    };
  }

  if (taskId) {
    questionnaireResponseData.extension = setExtensionValue(
      questionnaireResponseData.extension,
      Extensions.QuestionnaireResponseTaskId,
      { valueReference: { reference: joinReferenceString("Task", taskId) } },
    );
  }

  try {
    let questionnaireResponseResults: QuestionnaireResponse | undefined;
    if (questionnaireResponse.id) {
      questionnaireResponseResults = await medplum.updateResource({
        ...questionnaireResponseData,
        id: questionnaireResponse.id,
      });
    } else {
      questionnaireResponseResults = await medplum.createResource(questionnaireResponseData);
    }
    await setAccounts(medplum, questionnaireResponseResults, [], { extractFromPatient: true });
    return questionnaireResponseResults;
  } catch (err) {
    Sentry.captureException(err);
    showSystemErrorNotification();
  }
}

export function getQuestionnaireContext(
  context: UsageContext[] | undefined,
): FormsLibrarySection | null {
  if (!context?.length) return null;

  const sectionContext = context.find((item) => {
    const { system, code } = item.code;
    return (
      system === QuiltedHealthCodeSystems.FormsLibraryContext &&
      code === FormsLibraryContext.Section
    );
  });

  if (!sectionContext?.valueCodeableConcept?.coding?.[0]) return null;

  const coding = sectionContext.valueCodeableConcept.coding[0];

  if (coding.system !== QuiltedHealthCodeSystems.FormsLibrarySection) return null;

  return coding.code as FormsLibrarySection;
}

export const fetchConsentQuestionnaireInfoByQuestionnaireId = async (
  medplum: MedplumClient,
  questionnaireId: string,
  clientId: string,
) => {
  const questionnairesResults = await medplum.searchResources("Questionnaire", {
    context: FormsLibrarySection.ConsentForms,
    status: "active",
    _id: questionnaireId,
  });

  const questionnaire = getResourcesFromBundle<Questionnaire>(
    "Questionnaire",
    questionnairesResults.bundle,
  )?.[0];

  let previousConsentsByQuestionnaireId: Record<string, Consent[]> = {};

  if (questionnaire) {
    previousConsentsByQuestionnaireId = await fetchPreviousConsentsByQuestionnaireId(
      medplum,
      [questionnaireId],
      clientId,
    );
  }

  return { questionnaire, previousConsentsByQuestionnaireId };
};
